/**
 * Custom table resize for Jodit: row heights (horizontal hit band) and column widths
 * (vertical hit band). Row drag on an internal boundary grows the upper row only so the
 * table extends and rows below move down (no fixed-sum “steal” from the row beneath).
 * Corners pick row vs column by the closer edge distance.
 */

const NEARBY_PX = 6
const ROW_HANDLE_PX = 3
const COL_HANDLE_PX = 3
const MIN_ROW_PX = 28
const MIN_COL_PX = 32
const RESIZE_NS = '.tashkecTableResize'
const LOCK_KEY = 'tashkecTableResize'

type JoditEventBus = {
  on: (...args: unknown[]) => JoditEventBus
  off: (...args: unknown[]) => JoditEventBus
  fire: (ev: string, ...args: unknown[]) => unknown
}

export type JoditInstanceForTableResize = {
  editor: HTMLElement
  workplace: HTMLElement
  ew: Window
  ed: Document
  isLocked: boolean
  lock: (key?: string) => boolean
  unlock: () => boolean
  synchronizeValues: () => void
  defaultTimeout: number
  s: { sel: Selection | null; focus: () => void }
  async: {
    throttle: (fn: (e: MouseEvent) => void, opts: { timeout?: number }) => (e: MouseEvent) => void
    clearTimeout: (id: number) => void
    setTimeout: (fn: () => void, opts: { timeout: number; label?: string }) => number
  }
  e: JoditEventBus
  o?: { direction?: '' | 'ltr' | 'rtl' }
}

/** @deprecated use JoditInstanceForTableResize */
export type JoditInstanceForRowResize = JoditInstanceForTableResize

function isTableCell(el: unknown): el is HTMLTableCellElement {
  return el instanceof HTMLElement && (el.tagName === 'TD' || el.tagName === 'TH')
}

function closestCell(node: EventTarget | null, table: HTMLTableElement): HTMLTableCellElement | null {
  let el: Node | null = node as Node | null
  while (el && el !== table) {
    if (el instanceof Element && isTableCell(el)) {
      return el
    }
    el = el.parentNode
  }
  return null
}

function rowHasRowSpan(tr: HTMLTableRowElement): boolean {
  return Array.from(tr.cells).some(c => (c.rowSpan || 1) > 1)
}

function getRowHeight(tr: HTMLTableRowElement): number {
  return tr.getBoundingClientRect().height
}

function setTrHeight(tr: HTMLTableRowElement, px: number): void {
  tr.style.height = `${Math.max(MIN_ROW_PX, Math.round(px))}px`
}

function clientYFromMove(e: MouseEvent | TouchEvent): number {
  if ('touches' in e && e.touches.length > 0) return e.touches[0].clientY
  return (e as MouseEvent).clientY
}

function clientYFromEnd(e: MouseEvent | TouchEvent): number {
  if ('changedTouches' in e && e.changedTouches.length > 0) return e.changedTouches[0].clientY
  return (e as MouseEvent).clientY
}

function clientXFromMove(e: MouseEvent | TouchEvent): number {
  if ('touches' in e && e.touches.length > 0) return e.touches[0].clientX
  return (e as MouseEvent).clientX
}

function clientXFromEnd(e: MouseEvent | TouchEvent): number {
  if ('changedTouches' in e && e.changedTouches.length > 0) return e.changedTouches[0].clientX
  return (e as MouseEvent).clientX
}

type Matrix = (HTMLTableCellElement | null)[][]

function buildTableMatrix(table: HTMLTableElement): Matrix {
  const matrix: Matrix = []
  const rows = Array.from(table.rows)
  for (let i = 0; i < rows.length; i++) {
    if (!matrix[i]) matrix[i] = []
    let col = 0
    for (const cell of Array.from(rows[i].cells)) {
      while (matrix[i][col]) col++
      const cs = cell.colSpan || 1
      const rs = cell.rowSpan || 1
      for (let y = 0; y < rs; y++) {
        for (let x = 0; x < cs; x++) {
          const ri = i + y
          const cj = col + x
          if (!matrix[ri]) matrix[ri] = []
          matrix[ri][cj] = cell
        }
      }
    }
  }
  return matrix
}

function maxCols(matrix: Matrix): number {
  let m = 0
  for (const row of matrix) {
    if (row) m = Math.max(m, row.length)
  }
  return m
}

function formalColumnRangeForCell(matrix: Matrix, cell: HTMLTableCellElement, rowIndex: number): [number, number] {
  const row = matrix[rowIndex] || []
  let minJ = -1
  let maxJ = -1
  for (let j = 0; j < row.length; j++) {
    if (row[j] === cell) {
      if (minJ === -1) minJ = j
      maxJ = j
    }
  }
  return [minJ, maxJ]
}

function cellsAtFormalColumn(matrix: Matrix, colIndex: number): HTMLTableCellElement[] {
  const seen = new Set<HTMLTableCellElement>()
  const out: HTMLTableCellElement[] = []
  for (let r = 0; r < matrix.length; r++) {
    const cell = matrix[r]?.[colIndex]
    if (cell && !seen.has(cell)) {
      seen.add(cell)
      out.push(cell)
    }
  }
  return out
}

function avgCellWidth(cells: HTMLTableCellElement[]): number {
  if (!cells.length) return 0
  const sum = cells.reduce((s, c) => s + c.getBoundingClientRect().width, 0)
  return sum / cells.length
}

function setColCellsWidth(cells: HTMLTableCellElement[], px: number): void {
  const w = Math.max(MIN_COL_PX, Math.round(px))
  for (const c of cells) {
    c.style.width = `${w}px`
  }
}

export function registerJoditTableResize(editor: JoditInstanceForTableResize): () => void {
  const jev = editor.e as JoditEventBus
  const prev = registry.get(editor)
  prev?.()

  const isRtl = editor.o?.direction === 'rtl'

  let hideTimer = 0
  let drag = false
  let dragKind: 'row' | 'col' | null = null

  let startY = 0
  let resizeDeltaY = 0
  let upper: HTMLTableRowElement | null = null
  let lower: HTMLTableRowElement | null = null
  let hUpperStart = 0
  let hLowerStart = 0

  let startX = 0
  let resizeDeltaX = 0
  let leftColCells: HTMLTableCellElement[] = []
  let rightColCells: HTMLTableCellElement[] | null = null
  let baseLeftW = 0
  let baseRightW = 0

  let workTable: HTMLTableElement | null = null
  let rowHandle: HTMLDivElement | null = null
  let colHandle: HTMLDivElement | null = null

  const resetRowPair = (): void => {
    upper = null
    lower = null
    hUpperStart = 0
    hLowerStart = 0
  }

  const resetColPair = (): void => {
    leftColCells = []
    rightColCells = null
    baseLeftW = 0
    baseRightW = 0
  }

  const ensureRowHandle = (): HTMLDivElement => {
    if (!rowHandle) {
      rowHandle = editor.ed.createElement('div')
      rowHandle.className = 'jodit-tashkec-table-row-resizer'
      rowHandle.dataset.tashkecResize = 'row'
      jev.on(rowHandle, `mousedown${RESIZE_NS} touchstart${RESIZE_NS}`, onRowHandleDown as EventListener)
      jev.on(rowHandle, `mouseenter${RESIZE_NS}`, onHandleMouseEnter)
    }
    return rowHandle
  }

  const ensureColHandle = (): HTMLDivElement => {
    if (!colHandle) {
      colHandle = editor.ed.createElement('div')
      colHandle.className = 'jodit-tashkec-table-col-resizer'
      colHandle.dataset.tashkecResize = 'col'
      jev.on(colHandle, `mousedown${RESIZE_NS} touchstart${RESIZE_NS}`, onColHandleDown as EventListener)
      jev.on(colHandle, `mouseenter${RESIZE_NS}`, onHandleMouseEnter)
    }
    return colHandle
  }

  function onHandleMouseEnter(): void {
    editor.async.clearTimeout(hideTimer)
  }

  const removeRowHandle = (): void => {
    if (rowHandle?.parentNode) rowHandle.parentNode.removeChild(rowHandle)
  }

  const removeColHandle = (): void => {
    if (colHandle?.parentNode) colHandle.parentNode.removeChild(colHandle)
  }

  const scheduleHideHandles = (): void => {
    editor.async.clearTimeout(hideTimer)
    hideTimer = editor.async.setTimeout(
      () => {
        removeRowHandle()
        removeColHandle()
      },
      { timeout: editor.defaultTimeout, label: 'tashkecHideTableResizer' }
    )
  }

  const showRowHandle = (): void => {
    editor.async.clearTimeout(hideTimer)
    removeColHandle()
    const h = ensureRowHandle()
    if (!h.parentNode) editor.workplace.appendChild(h)
  }

  const showColHandle = (): void => {
    editor.async.clearTimeout(hideTimer)
    removeRowHandle()
    const h = ensureColHandle()
    if (!h.parentNode) editor.workplace.appendChild(h)
  }

  const workplaceRect = (): DOMRect => editor.workplace.getBoundingClientRect()

  const positionRowHandle = (table: HTMLTableElement, borderClientY: number): void => {
    const tableRect = table.getBoundingClientRect()
    const wp = workplaceRect()
    const h = ensureRowHandle()
    const top = borderClientY - wp.top - ROW_HANDLE_PX / 2
    const left = tableRect.left - wp.left
    Object.assign(h.style, {
      left: `${Math.round(left)}px`,
      width: `${Math.round(tableRect.width)}px`,
      top: `${Math.round(top)}px`,
      display: 'block',
    })
    showRowHandle()
  }

  const positionColHandle = (table: HTMLTableElement, borderClientX: number): void => {
    const tableRect = table.getBoundingClientRect()
    const wp = workplaceRect()
    const h = ensureColHandle()
    const left = borderClientX - wp.left - COL_HANDLE_PX / 2
    const top = tableRect.top - wp.top
    Object.assign(h.style, {
      left: `${Math.round(left)}px`,
      width: `${Math.round(COL_HANDLE_PX)}px`,
      height: `${Math.round(tableRect.height)}px`,
      top: `${Math.round(top)}px`,
      display: 'block',
    })
    showColHandle()
  }

  const detachGlobalPointer = (): void => {
    try {
      jev.off(editor.ew, `mousemove${RESIZE_NS}`, onRowPointerMove as EventListener)
      jev.off(editor.ew, `touchmove${RESIZE_NS}`, onRowPointerMove as EventListener)
      jev.off(editor.ew, `mouseup${RESIZE_NS}`, onRowPointerUp as EventListener)
      jev.off(editor.ew, `touchend${RESIZE_NS}`, onRowPointerUp as EventListener)
      jev.off(editor.ew, `mousemove${RESIZE_NS}`, onColPointerMove as EventListener)
      jev.off(editor.ew, `touchmove${RESIZE_NS}`, onColPointerMove as EventListener)
      jev.off(editor.ew, `mouseup${RESIZE_NS}`, onColPointerUp as EventListener)
      jev.off(editor.ew, `touchend${RESIZE_NS}`, onColPointerUp as EventListener)
    } catch {
      /* Jodit may already be tearing down; EventEmitter.off can throw "Need subject". */
    }
  }

  const hideResizeState = (): void => {
    drag = false
    dragKind = null
    rowHandle?.classList.remove('jodit-tashkec-table-row-resizer_moved')
    colHandle?.classList.remove('jodit-tashkec-table-col-resizer_moved')
    detachGlobalPointer()
    editor.unlock()
  }

  /**
   * Row resize is additive on the row *above* the dragged boundary (see prepareRowDrag):
   * dragging the line down increases that row’s height and grows the table so rows below
   * shift down. We do not steal height from the next row (that capped growth as soon as
   * the neighbor hit MIN_ROW_PX).
   */
  const applyRowResize = (delta: number): void => {
    if (upper && lower) {
      setTrHeight(upper, hUpperStart + delta)
      lower.style.height = ''
      lower.style.minHeight = ''
      return
    }
    if (upper && !lower) {
      setTrHeight(upper, hUpperStart + delta)
    }
  }

  const applyColResize = (delta: number): void => {
    const d = isRtl ? -delta : delta
    if (leftColCells.length && rightColCells && rightColCells.length) {
      const sum = baseLeftW + baseRightW
      let nl = baseLeftW + d
      let nr = baseRightW - d
      if (nl < MIN_COL_PX) {
        nl = MIN_COL_PX
        nr = sum - nl
      }
      if (nr < MIN_COL_PX) {
        nr = MIN_COL_PX
        nl = sum - nr
      }
      setColCellsWidth(leftColCells, nl)
      setColCellsWidth(rightColCells, nr)
      return
    }
    if (leftColCells.length && !rightColCells) {
      setColCellsWidth(leftColCells, Math.max(MIN_COL_PX, baseLeftW + d))
    }
  }

  function onRowPointerMove(event: MouseEvent | TouchEvent): void {
    if (!drag || dragKind !== 'row') return
    resizeDeltaY = clientYFromMove(event) - startY
    if (rowHandle) {
      const baseTop = parseFloat(rowHandle.dataset.baseTop || '0')
      rowHandle.style.top = `${Math.round(baseTop + resizeDeltaY)}px`
    }
    jev.fire('closeAllPopups')
    editor.s.sel?.removeAllRanges()
  }

  function onRowPointerUp(event: MouseEvent | TouchEvent): void {
    const clientY = clientYFromEnd(event)
    hideResizeState()
    if (startY !== clientY && workTable && (upper || lower)) {
      applyRowResize(resizeDeltaY)
      editor.synchronizeValues()
    }
    resetRowPair()
    resetColPair()
    workTable = null
    editor.s.focus()
    scheduleHideHandles()
  }

  function onRowHandleDown(event: MouseEvent | TouchEvent): boolean | void {
    if (editor.isLocked) return
    event.preventDefault()
    event.stopPropagation()
    const clientY = 'touches' in event && event.touches.length > 0 ? event.touches[0].clientY : (event as MouseEvent).clientY
    drag = true
    dragKind = 'row'
    startY = clientY
    resizeDeltaY = 0
    if (rowHandle) {
      rowHandle.dataset.baseTop = String(parseFloat(rowHandle.style.top || '0'))
      rowHandle.classList.add('jodit-tashkec-table-row-resizer_moved')
    }
    editor.lock(LOCK_KEY)
    jev
      .on(editor.ew, `mousemove${RESIZE_NS}`, onRowPointerMove as EventListener)
      .on(editor.ew, `touchmove${RESIZE_NS}`, onRowPointerMove as EventListener)
      .on(editor.ew, `mouseup${RESIZE_NS}`, onRowPointerUp as EventListener)
      .on(editor.ew, `touchend${RESIZE_NS}`, onRowPointerUp as EventListener)
    return false
  }

  function onColPointerMove(event: MouseEvent | TouchEvent): void {
    if (!drag || dragKind !== 'col') return
    resizeDeltaX = clientXFromMove(event) - startX
    if (colHandle) {
      const baseLeft = parseFloat(colHandle.dataset.baseLeft || '0')
      colHandle.style.left = `${Math.round(baseLeft + resizeDeltaX)}px`
    }
    jev.fire('closeAllPopups')
    editor.s.sel?.removeAllRanges()
  }

  function onColPointerUp(event: MouseEvent | TouchEvent): void {
    const clientX = clientXFromEnd(event)
    hideResizeState()
    if (startX !== clientX && workTable && leftColCells.length) {
      applyColResize(resizeDeltaX)
      editor.synchronizeValues()
    }
    resetRowPair()
    resetColPair()
    workTable = null
    editor.s.focus()
    scheduleHideHandles()
  }

  function onColHandleDown(event: MouseEvent | TouchEvent): boolean | void {
    if (editor.isLocked) return
    event.preventDefault()
    event.stopPropagation()
    const clientX = 'touches' in event && event.touches.length > 0 ? event.touches[0].clientX : (event as MouseEvent).clientX
    drag = true
    dragKind = 'col'
    startX = clientX
    resizeDeltaX = 0
    if (colHandle) {
      colHandle.dataset.baseLeft = String(parseFloat(colHandle.style.left || '0'))
      colHandle.classList.add('jodit-tashkec-table-col-resizer_moved')
    }
    editor.lock(LOCK_KEY)
    jev
      .on(editor.ew, `mousemove${RESIZE_NS}`, onColPointerMove as EventListener)
      .on(editor.ew, `touchmove${RESIZE_NS}`, onColPointerMove as EventListener)
      .on(editor.ew, `mouseup${RESIZE_NS}`, onColPointerUp as EventListener)
      .on(editor.ew, `touchend${RESIZE_NS}`, onColPointerUp as EventListener)
    return false
  }

  const prepareRowDrag = (cell: HTMLTableCellElement, edge: 'top' | 'bottom', borderClientY: number): void => {
    resetRowPair()
    resetColPair()
    const tr = cell.parentElement as HTMLTableRowElement
    const tbl = tr.closest('table')
    if (!tbl || rowHasRowSpan(tr)) {
      scheduleHideHandles()
      return
    }
    workTable = tbl
    if (edge === 'top') {
      const prev = tr.previousElementSibling as HTMLTableRowElement | null
      if (prev && !rowHasRowSpan(prev)) {
        upper = prev
        lower = tr
        hUpperStart = getRowHeight(prev)
        hLowerStart = getRowHeight(tr)
      } else {
        upper = tr
        lower = null
        hUpperStart = getRowHeight(tr)
      }
    } else {
      const next = tr.nextElementSibling as HTMLTableRowElement | null
      if (next && !rowHasRowSpan(next)) {
        upper = tr
        lower = next
        hUpperStart = getRowHeight(tr)
        hLowerStart = getRowHeight(next)
      } else {
        upper = tr
        lower = null
        hUpperStart = getRowHeight(tr)
      }
    }
    positionRowHandle(tbl, borderClientY)
  }

  const prepareColDrag = (
    table: HTMLTableElement,
    matrix: Matrix,
    cell: HTMLTableCellElement,
    edge: 'left' | 'right',
    borderClientX: number
  ): void => {
    resetRowPair()
    resetColPair()
    const tr = cell.parentElement as HTMLTableRowElement
    const rowIndex = Array.from(table.rows).indexOf(tr)
    if (rowIndex < 0) {
      scheduleHideHandles()
      return
    }
    const [jMin, jMax] = formalColumnRangeForCell(matrix, cell, rowIndex)
    if (jMin < 0 || jMax < 0) {
      scheduleHideHandles()
      return
    }
    const n = maxCols(matrix)
    workTable = table

    if (edge === 'left') {
      if (jMin <= 0) {
        leftColCells = cellsAtFormalColumn(matrix, 0)
        rightColCells = null
        baseLeftW = avgCellWidth(leftColCells)
      } else {
        leftColCells = cellsAtFormalColumn(matrix, jMin - 1)
        rightColCells = cellsAtFormalColumn(matrix, jMin)
        if (!leftColCells.length || !rightColCells.length) {
          scheduleHideHandles()
          return
        }
        baseLeftW = avgCellWidth(leftColCells)
        baseRightW = avgCellWidth(rightColCells)
      }
    } else {
      if (jMax >= n - 1) {
        leftColCells = cellsAtFormalColumn(matrix, n - 1)
        rightColCells = null
        baseLeftW = avgCellWidth(leftColCells)
      } else {
        leftColCells = cellsAtFormalColumn(matrix, jMax)
        rightColCells = cellsAtFormalColumn(matrix, jMax + 1)
        if (!leftColCells.length || !rightColCells.length) {
          scheduleHideHandles()
          return
        }
        baseLeftW = avgCellWidth(leftColCells)
        baseRightW = avgCellWidth(rightColCells)
      }
    }
    positionColHandle(table, borderClientX)
  }

  const onTableMouseMove = (table: HTMLTableElement) => (event: MouseEvent) => {
    if (editor.isLocked || drag) return
    const cell = closestCell(event.target, table)
    if (!cell) {
      scheduleHideHandles()
      return
    }
    const tr = cell.parentElement as HTMLTableRowElement
    if (rowHasRowSpan(tr)) {
      scheduleHideHandles()
      return
    }

    const rect = cell.getBoundingClientRect()
    const xInCell = isRtl ? rect.right - event.clientX : event.clientX - rect.left
    const yInCell = event.clientY - rect.top
    const w = rect.width
    const h = rect.height

    const nearTop = yInCell <= NEARBY_PX
    const nearBottom = yInCell >= h - NEARBY_PX
    const nearLeft = xInCell <= NEARBY_PX
    const nearRight = xInCell >= w - NEARBY_PX
    const distH = Math.min(yInCell, h - yInCell)
    const distV = Math.min(xInCell, w - xInCell)

    let mode: 'row' | 'col' | null = null
    if (nearTop || nearBottom || nearLeft || nearRight) {
      const corner = (nearTop || nearBottom) && (nearLeft || nearRight)
      if (corner) {
        mode = distH < distV ? 'row' : 'col'
      } else if (nearTop || nearBottom) {
        mode = 'row'
      } else {
        mode = 'col'
      }
    }

    if (mode === 'row') {
      if (!nearTop && !nearBottom) {
        scheduleHideHandles()
        return
      }
      let edge: 'top' | 'bottom'
      let borderClientY: number
      if (nearTop && !nearBottom) {
        edge = 'top'
        borderClientY = rect.top
      } else if (!nearTop && nearBottom) {
        edge = 'bottom'
        borderClientY = rect.bottom
      } else {
        edge = yInCell <= h / 2 ? 'top' : 'bottom'
        borderClientY = edge === 'top' ? rect.top : rect.bottom
      }
      prepareRowDrag(cell, edge, borderClientY)
      return
    }

    if (mode === 'col') {
      if (!nearLeft && !nearRight) {
        scheduleHideHandles()
        return
      }
      let edge: 'left' | 'right'
      let borderClientX: number
      if (nearLeft && !nearRight) {
        edge = 'left'
        borderClientX = rect.left
      } else if (!nearLeft && nearRight) {
        edge = 'right'
        borderClientX = rect.right
      } else {
        edge = xInCell <= w / 2 ? 'left' : 'right'
        borderClientX = edge === 'left' ? rect.left : rect.right
      }
      const matrix = buildTableMatrix(table)
      prepareColDrag(table, matrix, cell, edge, borderClientX)
      return
    }

    scheduleHideHandles()
  }

  const onTableMouseLeave = (e: MouseEvent): void => {
    const rel = e.relatedTarget as Node | null
    if ((rowHandle && rel && rowHandle.contains(rel)) || (colHandle && rel && colHandle.contains(rel))) {
      return
    }
    if (!drag) scheduleHideHandles()
  }

  const bindTable = (table: HTMLTableElement): void => {
    if ((table as HTMLTableElement & { __tashkecTableResize?: boolean }).__tashkecTableResize) return
    ;(table as HTMLTableElement & { __tashkecTableResize?: boolean }).__tashkecTableResize = true
    const throttled = editor.async.throttle(onTableMouseMove(table), { timeout: editor.defaultTimeout })
    tableThrottledMove.set(table, throttled)
    jev.on(table, `mouseleave${RESIZE_NS}`, onTableMouseLeave).on(table, `mousemove${RESIZE_NS}`, throttled)
  }

  const scanTables = (): void => {
    editor.editor.querySelectorAll('table').forEach(t => bindTable(t as HTMLTableElement))
  }

  scanTables()

  jev
    .on(`change${RESIZE_NS}`, scanTables)
    .on(`afterCommand${RESIZE_NS}`, scanTables)
    .on(`afterSetMode${RESIZE_NS}`, scanTables)

  const onBeforeDestruct = (): void => {
    cleanup()
  }
  jev.on(`beforeDestruct${RESIZE_NS}`, onBeforeDestruct)

  function cleanup(): void {
    try {
      jev.off(`beforeDestruct${RESIZE_NS}`, onBeforeDestruct)
      jev.off(`change${RESIZE_NS}`, scanTables)
      jev.off(`afterCommand${RESIZE_NS}`, scanTables)
      jev.off(`afterSetMode${RESIZE_NS}`, scanTables)
      detachGlobalPointer()
      editor.editor.querySelectorAll('table').forEach(t => {
      const table = t as HTMLTableElement
      const throttled = tableThrottledMove.get(table)
      jev.off(table, `mouseleave${RESIZE_NS}`, onTableMouseLeave as EventListener)
      if (throttled) jev.off(table, `mousemove${RESIZE_NS}`, throttled as unknown as EventListener)
      tableThrottledMove.delete(table)
      delete (table as HTMLTableElement & { __tashkecTableResize?: boolean }).__tashkecTableResize
    })
    if (rowHandle) {
      jev.off(rowHandle, `mousedown${RESIZE_NS}`)
      jev.off(rowHandle, `touchstart${RESIZE_NS}`)
      jev.off(rowHandle, `mouseenter${RESIZE_NS}`)
      removeRowHandle()
      rowHandle = null
    }
    if (colHandle) {
      jev.off(colHandle, `mousedown${RESIZE_NS}`)
      jev.off(colHandle, `touchstart${RESIZE_NS}`)
      jev.off(colHandle, `mouseenter${RESIZE_NS}`)
      removeColHandle()
      colHandle = null
    }
      editor.async.clearTimeout(hideTimer)
    } catch {
      /* Safe teardown when Jodit is mid-destruct. */
    } finally {
      registry.delete(editor)
    }
  }

  const dispose = (): void => {
    cleanup()
  }
  registry.set(editor, dispose)
  return dispose
}

/** @deprecated use registerJoditTableResize */
export const registerJoditTableRowResize = registerJoditTableResize

const registry = new WeakMap<JoditInstanceForTableResize, () => void>()
const tableThrottledMove = new WeakMap<HTMLTableElement, (e: MouseEvent) => void>()
